import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TenantProvider, useTenant } from './context/TenantContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import ProjectPage from './pages/ProjectPage';
import AdminPage from './pages/AdminPage';
import SuperAdminPage from './pages/SuperAdminPage';
import TenantDashboardPage from './pages/TenantDashboardPage';
import PartnerPortalPage from './pages/PartnerPortalPage';
import TenantOnboardingPage from './pages/TenantOnboardingPage';
import PublicDocsPage from './pages/PublicDocsPage';
import BrandLogo from './components/BrandLogo';
import './styles/global.css';

function Loader() {
  return (
    <div style={{ height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--bg)',flexDirection:'column',gap:14 }}>
      <BrandLogo height={44} />
      <div style={{ fontSize:12,color:'var(--text-3)' }}>Loading RechargeKit...</div>
    </div>
  );
}

function PrivateRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

// Super-admins always land on the platform console.
function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'super_admin') return <Navigate to="/" replace />;
  return children;
}

// Routes that require an active tenant context (Business Admin, Team Manager, Agent, API Partner).
function TenantRoute({ children }) {
  const { user, loading } = useAuth();
  const { activeTenant } = useTenant();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!activeTenant) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return children;
  // Super-admins skip straight to the platform console after login.
  if (user.role === 'super_admin') return <Navigate to="/platform" replace />;
  return <Navigate to="/" replace />;
}

// Toasts render in a portal outside the themed tree, so they take their
// colours from the theme directly rather than from CSS variables.
function ThemedToaster() {
  const { isDark } = useTheme();
  const style = isDark
    ? { background:'#1e1e2e', color:'#cdd6f4', border:'1px solid #313244' }
    : { background:'#ffffff', color:'#111827', border:'1px solid #e5e8ef', boxShadow:'0 4px 14px rgba(15,23,42,0.10)' };
  return (
    <Toaster position="bottom-right" toastOptions={{
      style: { ...style, fontFamily:'Inter,sans-serif', fontSize:13, borderRadius:8 },
      success: { iconTheme: { primary: isDark ? '#a6e3a1' : '#059669', secondary: style.background } },
      error:   { iconTheme: { primary: isDark ? '#f38ba8' : '#dc2626', secondary: style.background } },
    }} />
  );
}

export default function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <TenantProvider>
        <BrowserRouter>
          <ThemedToaster />
          <Routes>
            {/* Public documentation — no authentication required */}
            <Route path="/docs/:slug"                 element={<PublicDocsPage />} />
            <Route path="/docs/:slug/:endpointSlug"   element={<PublicDocsPage />} />
            <Route path="/share/:token"               element={<PublicDocsPage />} />
            <Route path="/share/:token/:endpointSlug" element={<PublicDocsPage />} />

            <Route path="/login" element={<PublicRoute><AuthPage /></PublicRoute>} />

            {/* API workspace (existing product) */}
            <Route path="/"            element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
            <Route path="/project/:id" element={<PrivateRoute><ProjectPage /></PrivateRoute>} />
            <Route path="/admin"       element={<PrivateRoute roles={['super_admin','admin','member']}><AdminPage /></PrivateRoute>} />

            {/* Enterprise SaaS layer */}
            <Route path="/platform"   element={<SuperAdminRoute><SuperAdminPage /></SuperAdminRoute>} />
            <Route path="/onboarding" element={<PrivateRoute><TenantOnboardingPage /></PrivateRoute>} />
            <Route path="/tenant"     element={<TenantRoute><TenantDashboardPage /></TenantRoute>} />
            <Route path="/partner"    element={<TenantRoute><PartnerPortalPage /></TenantRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </TenantProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}
